// only for doctor
import { FontAwesome, MaterialIcons } from "@expo/vector-icons";
import React, { useEffect, useMemo } from "react";
import {
  Dimensions,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Animated,
} from "react-native";
import { StepHeader, Button, Row, Centered, Input } from "../../../components/all";
import colors from "../../../constants/colors";
import Styles from "../../../constants/styles";
import { useDispatch, useSelector } from "react-redux";
import { addFields } from "../../../reducers/auth/register";
import Collapsible from "react-native-collapsible";
import { useQuery } from "@tanstack/react-query";
import { getFacilityTypes } from "../../../services/shared";
import { useFacilityData } from "../../../hooks/useFacilityData";
import { extractFacilityTitleAndSubtitle } from "../../../utils/facilityUtils";

export default function StepFacility({ navigation, setField, route }) {
  const dispatch = useDispatch();
  const [expandedTypes, setExpandedTypes] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");
  const [isShow, setIsShow] = React.useState(false);
  const [animatedValues, setAnimatedValues] = React.useState({});
  const [arrowAnimations, setArrowAnimations] = React.useState({});
  const jobType = route?.params?.jobType;
  const isFullTime = jobType == 2;
  useEffect(() => {
    if (isFullTime) {
      let fields = {
        type: -1,
      };
      navigation.navigate("RegisterUserScreen", {
        jobType: jobType,
      });
      dispatch(addFields(fields));
    } else {
      setIsShow(true);
    }
  }, [isFullTime]);
  console.log(isFullTime);
  const input = {
    emailCode: "emailCode",
    isGP: "isGP",
    isSpecialist: "isSpecialist",
  };
  const { isDev } = useSelector((state) => state.user);
  const { country: userCountry } = useSelector((state) => state.user || {});
  const registerCountry = useSelector((state) => state.auth?.register?.country);
  const country = registerCountry || userCountry || 'MY';

  const [selectedFacility, setSelectedFacility] = React.useState(null);
  const fromPostJob = route?.name === "PostJobScreen";

  // Icon mapping for facility types
  const facilityIcons = {
    'hospital': { icon: 'local-hospital', color: '#4B9BFF', bgColor: '#E3F2FF' },
    'clinic': { icon: 'medical-services', color: '#10B981', bgColor: '#D1FAE5' },
    'pharmacy': { icon: 'medication', color: '#F59E0B', bgColor: '#FEF3C7' },
    'lab': { icon: 'biotech', color: '#8B5CF6', bgColor: '#EDE9FE' },
    'nursing': { icon: 'healing', color: '#EC4899', bgColor: '#FCE7F3' },
    'rehab': { icon: 'accessible', color: '#06B6D4', bgColor: '#E0F2FE' },
    'default': { icon: 'health-and-safety', color: colors.primary, bgColor: colors.primary + '10' }
  };

  // Use new facility data hook with fallback to old system
  const {
    data: newFacilityData,
    isLoading: isLoadingNew,
    error: errorNew,
    refetch: refetchNew,
    isUsingNewSystem
  } = useFacilityData(country);

  // Fallback to old API if new system not available
  const { data: facilityData, isLoading: isLoadingOld, error: errorOld, refetch: refetchOld } = useQuery(
    ['facilityTypes', country],
    () => getFacilityTypes(country),
    {
      enabled: !isUsingNewSystem,
      staleTime: 0,
      cacheTime: 0,
      retry: 2,
      onError: (error) => {
        console.error('Failed to fetch facility types:', error);
      }
    }
  );

  const isLoading = isLoadingNew || isLoadingOld;
  const error = errorNew && errorOld;
  const refetch = isUsingNewSystem ? refetchNew : refetchOld;

  // Use new data if available, otherwise fallback to old
  const facilities = newFacilityData || facilityData?.data?.data || [];

  // Get icon for facility based on name
  const getFacilityIcon = (facilityName) => {
    const lowerName = facilityName.toLowerCase();
    if (lowerName.includes('hospital')) return facilityIcons.hospital;
    if (lowerName.includes('clinic')) return facilityIcons.clinic;
    if (lowerName.includes('pharmacy')) return facilityIcons.pharmacy;
    if (lowerName.includes('lab')) return facilityIcons.lab;
    if (lowerName.includes('nursing')) return facilityIcons.nursing;
    if (lowerName.includes('rehab')) return facilityIcons.rehab;
    return facilityIcons.hospital;
  };

  const toggleExpanded = (facilityId) => {
    setExpandedTypes(prev => {
      const isExpanding = !prev.includes(facilityId);
      
      // Animate arrow rotation
      if (arrowAnimations[facilityId]) {
        Animated.spring(arrowAnimations[facilityId], {
          toValue: isExpanding ? 1 : 0,
          useNativeDriver: true,
          tension: 50,
          friction: 7,
        }).start();
      }
      
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      }
      return [...prev, facilityId];
    });
  };

  const selectFacility = (item) => {
    setSelectedFacility(item);
    // Store fac_id if using new system and item has it
    if (isUsingNewSystem && item.fac_id) {
      const fields = {
        type: item.id,
        fac_id: item.fac_id,
      };
      dispatch(addFields(fields));
    }
  };

  const filteredFacilities = useMemo(() => {
    if (!searchText) return facilities;
    
    const lowerSearch = searchText.toLowerCase();
    return facilities.filter((facility) => {
      const matchesName = facility.name.toLowerCase().includes(lowerSearch);
      
      // Check if any types match the search
      const hasMatchingType = facility.types?.some(type =>
        type.name.toLowerCase().includes(lowerSearch)
      );
      
      return matchesName || hasMatchingType;
    });
  }, [facilities, searchText]);

  // Initialize animations for each facility
  useEffect(() => {
    const newAnimatedValues = {};
    const newArrowAnimations = {};
    
    filteredFacilities.forEach((facility, index) => {
      if (!animatedValues[facility.id]) {
        newAnimatedValues[facility.id] = new Animated.Value(0);
        newArrowAnimations[facility.id] = new Animated.Value(0);
      }
    });
    
    if (Object.keys(newAnimatedValues).length > 0) {
      setAnimatedValues(prev => ({ ...prev, ...newAnimatedValues }));
      setArrowAnimations(prev => ({ ...prev, ...newArrowAnimations }));
    }
  }, [filteredFacilities]);

  // Animate entrance when data loads
  useEffect(() => {
    if (!isLoading && Object.keys(animatedValues).length > 0) {
      const animations = Object.entries(animatedValues).map(([id, animValue], index) => {
        return Animated.timing(animValue, {
          toValue: 1,
          duration: 400,
          delay: index * 100,
          useNativeDriver: true,
        });
      });
      
      Animated.parallel(animations).start();
    }
  }, [isLoading, animatedValues]);

  if (!isShow) return null;

  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ marginTop: 16 }}>Loading facility types...</Text>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
          Unable to load facility types. Please check your internet connection and try again.
        </Text>
        <Button onPress={() => refetch()} label="Retry" type="primary" />
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={{
          ...Styles.webSmallContainer,
          marginTop: fromPostJob ? 48 : 80,
        }}
      >
        <StepHeader
          label={`Select Facility Type`}
          icon={<MaterialIcons name="business" size={24} color="black" />}
          subtitle={fromPostJob ? `Select the facility type you are hiring for` : `Select your medical facility type`}
        />
        <View style={{
          marginHorizontal: 16,
          marginTop: 32,
        }}>
          <Input
            placeholder="Search Facility Type"
            onChangeText={(text) => {
              setSearchText(text);
              // Auto-expand when searching
              if (text.length > 0) {
                setExpandedTypes(filteredFacilities.map(f => f.id));
              } else {
                setExpandedTypes([]);
              }
            }}
          />
        </View>
        <View
          style={{
            width: Dimensions.get("window").width,
            paddingHorizontal: 16,
            marginTop: 16,
          }}
        >
          {filteredFacilities.map((facility, index) => {
            const isExpanded = expandedTypes.includes(facility.id) || searchText.length > 0;
            const iconConfig = getFacilityIcon(facility.name);
            const animValue = animatedValues[facility.id] || new Animated.Value(1);
            const arrowAnim = arrowAnimations[facility.id] || new Animated.Value(0);
            const hasSubtypes = facility.types && facility.types.length > 0;
            
            return (
              <Animated.View
                key={facility.id}
                style={{
                  marginBottom: 16,
                  opacity: animValue,
                  transform: [
                    {
                      translateY: animValue.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      }),
                    },
                  ],
                }}
              >
                {/* Parent Facility */}
                <TouchableOpacity
                  onPress={() => {
                    if (hasSubtypes) {
                      toggleExpanded(facility.id);
                    } else {
                      selectFacility(facility);
                    }
                  }}
                  activeOpacity={0.7}
                  style={{
                    backgroundColor: isExpanded ? iconConfig.bgColor : "white",
                    paddingHorizontal: 20,
                    paddingVertical: 18,
                    borderRadius: 20,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderWidth: 2,
                    borderColor: (!hasSubtypes && selectedFacility?.id === facility.id) ? iconConfig.color : (isExpanded ? iconConfig.color : "#F3F4F6"),
                    shadowColor: "#000",
                    shadowOffset: {
                      width: 0,
                      height: 4,
                    },
                    shadowOpacity: isExpanded ? 0.15 : 0.08,
                    shadowRadius: 12,
                    elevation: 3,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <View
                      style={{
                        width: 48,
                        height: 48,
                        backgroundColor: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color : iconConfig.bgColor,
                        borderRadius: 24,
                        marginRight: 16,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <MaterialIcons
                        name={iconConfig.icon}
                        size={24}
                        color={(isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? "white" : iconConfig.color}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      {(() => {
                        const { title, subtitle } = extractFacilityTitleAndSubtitle(facility.name);
                        return (
                          <>
                            <Text style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color : "#1F2937",
                              letterSpacing: -0.5,
                            }}>
                              {title}
                            </Text>
                            {subtitle && (
                              <Text style={{
                                fontSize: 14,
                                fontWeight: "500",
                                color: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color + 'CC' : "#6B7280",
                                letterSpacing: -0.2,
                                marginTop: 2,
                              }}>
                                {subtitle}
                              </Text>
                            )}
                          </>
                        );
                      })()}
                      {hasSubtypes && (
                        <Text style={{
                          fontSize: 13,
                          color: isExpanded ? "#4B5563" : "#6B7280",
                          marginTop: 4,
                          letterSpacing: -0.1,
                        }}>
                          {facility.types.length} type{facility.types.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                    </View>
                  </View>
                  {hasSubtypes && (
                    <Animated.View
                      style={{
                        transform: [
                          {
                            rotate: arrowAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0deg', '90deg'],
                            }),
                          },
                        ],
                      }}
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={24}
                        color={isExpanded ? iconConfig.color : "#9CA3AF"}
                      />
                    </Animated.View>
                  )}
                  {!hasSubtypes && selectedFacility?.id === facility.id && (
                    <MaterialIcons
                      name="check-circle"
                      size={24}
                      color={iconConfig.color}
                    />
                  )}
                </TouchableOpacity>

                {/* Subtypes */}
                {hasSubtypes && (
                  <Collapsible collapsed={!isExpanded}>
                    <View style={{ marginTop: 12, marginLeft: 0 }}>
                      {facility.types.map((subtype, subtypeIndex) => {
                        const isSelected = selectedFacility?.id === subtype.id;
                        return (
                          <Animated.View
                            key={subtype.id}
                            style={{
                              opacity: isExpanded ? 1 : 0,
                              transform: [
                                {
                                  translateX: isExpanded ? 0 : -20,
                                },
                                {
                                  scale: isSelected ? 0.98 : 1,
                                },
                              ],
                            }}
                          >
                            <TouchableOpacity
                              onPress={() => selectFacility(subtype)}
                              activeOpacity={0.7}
                              style={{
                                backgroundColor: isSelected
                                  ? iconConfig.color + '15'
                                  : "white",
                                paddingHorizontal: 20,
                                paddingVertical: 16,
                                marginBottom: 8,
                                borderRadius: 16,
                                borderWidth: 2,
                                borderColor: isSelected
                                  ? iconConfig.color
                                  : "#E5E7EB",
                                flexDirection: 'row',
                                alignItems: 'center',
                                shadowColor: "#000",
                                shadowOffset: {
                                  width: 0,
                                  height: 2,
                                },
                                shadowOpacity: isSelected ? 0.1 : 0.05,
                                shadowRadius: 6,
                                elevation: isSelected ? 2 : 1,
                              }}
                            >
                              <View
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 12,
                                  borderWidth: 2,
                                  borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                                  backgroundColor: isSelected ? iconConfig.color : "white",
                                  marginRight: 12,
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                {isSelected && (
                                  <MaterialIcons
                                    name="check"
                                    size={16}
                                    color="white"
                                  />
                                )}
                              </View>
                              {(() => {
                                const { title: subtypeTitle, subtitle: subtypeSubtitle } = extractFacilityTitleAndSubtitle(subtype.name);
                                return (
                                  <View style={{ flex: 1 }}>
                                    <Text style={{
                                      fontSize: 15,
                                      color: isSelected
                                        ? iconConfig.color
                                        : "#374151",
                                      fontWeight: isSelected
                                        ? "600"
                                        : "400",
                                      letterSpacing: -0.2,
                                    }}>
                                      {subtypeTitle}
                                    </Text>
                                    {subtypeSubtitle && (
                                      <Text style={{
                                        fontSize: 12,
                                        color: isSelected
                                          ? iconConfig.color + 'CC'
                                          : "#6B7280",
                                        fontWeight: "400",
                                        letterSpacing: -0.1,
                                        marginTop: 2,
                                      }}>
                                        {subtypeSubtitle}
                                      </Text>
                                    )}
                                  </View>
                                );
                              })()}
                            </TouchableOpacity>
                          </Animated.View>
                        );
                      })}
                    </View>
                  </Collapsible>
                )}
              </Animated.View>
            );
          })}
        </View>
        <View style={{ marginBottom: 48 }} />
      </ScrollView>
      <Centered
        style={{
          position: "fixed",
          bottom: 32,
          backgroundColor: "white",
          width: "100%",
          paddingHorizontal: 24,
        }}
      >
        <Row style={{ marginBottom: 24 }}>
          <Button
            label="Back"
            type={"secondary"}
            onPress={() => {
              if (!isDev) {
                navigation.pop(2);
              } else {
                navigation.goBack();
              }
            }}
          />
          <Button
            label="Next"
            type={"primary"}
            onPress={() => {
              if (selectedFacility) {
                let fields = {
                  type: selectedFacility.id,
                };

                // Add fac_id if using new system
                if (isUsingNewSystem && selectedFacility.fac_id) {
                  fields.fac_id = selectedFacility.fac_id;
                }

                navigation.navigate("RegisterUserScreen", {
                  jobType: jobType,
                });
                dispatch(addFields(fields));
              } else {
                alert("Please select a facility type");
              }
            }}
          />
        </Row>
      </Centered>
    </>
  );
}

const styles = {
  category: {
    height: 64,
    justifyContent: "center",
    margin: 6,
    marginTop: 10,
    paddingHorizontal: 24,
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 18,
    width: "80%",
  },
};
